import { useMemo } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  subscriptionManageButtonAction,
  subscriptionSubscribeButtonAction,
} from 'app/store/Mixpanel/slice';
import {
  getStorage,
  getUpgradePopupOverlay,
} from 'app/store/Storage/selectors';
import { manageSubscriptionEffect } from 'app/store/Subscription/effects';
import {
  getDefaultCurrency,
  getSubscriptionTiersDDL,
} from 'app/store/Subscription/selectors';
import { setStorageQuotaStatus } from 'app/utils/helpers';
import { getPrice, openURL } from 'app/utils/utils';
import { PricingInterval } from 'types/subscription.types';
import { NextTierData } from 'app/components/Song/Audio/AudioList/types';
import { CreateSubscriptionOverlay } from 'app/components/Subscription';
import { useSubscription } from 'app/components/Subscription/hooks';
import { setShowUpgradePopup } from 'app/store/Storage/slice';
import { setShareModal } from 'app/store/Shares/slice';
import { StorageNotificationDialog } from '../StorageNotificationDialog';
import { PopupMessageType } from '../types';
import {
  bytesToGigabytes,
  getNextTier,
  getTextByType,
  getTierLabel,
} from '../utils';

interface StoragePopUpProps {
  messageType?: PopupMessageType;
  storageOverTheLimitProps: {
    isOpen: boolean;
    onClose(): void;
    onOpen(): void;
  };
}

export const StoragePopUp = ({
  messageType,
  storageOverTheLimitProps,
}: StoragePopUpProps) => {
  const dispatch = useAppDispatch();
  const defaultCurrency = useAppSelector(getDefaultCurrency);
  const subscriptionTiersDDL = useAppSelector(getSubscriptionTiersDDL);
  const storage = useAppSelector(getStorage);
  const disablePopupOverlay = useAppSelector(getUpgradePopupOverlay);

  const {
    isOpen: openSubscriptionOverlay,
    onOpen: setOpenSubscriptionOverlay,
    onClose: onSubscriptionOverlayClose,
  } = useDisclosure();

  const handleNotificationClose = () => {
    dispatch(setShowUpgradePopup(undefined));
    storageOverTheLimitProps.onClose?.();
  };

  const handleSubscriptionClose = () => {
    dispatch(setShowUpgradePopup(undefined));
    onSubscriptionOverlayClose();
  };

  const { subscription, tierList, manageSubscriptionLink } = useSubscription();
  const nextTier: NextTierData | undefined = useMemo(() => {
    const interval: PricingInterval = 'month';

    const getPriceLabel = (nextTier, currency) => {
      const pricePlan = nextTier.pricingPlans.find((plan) => {
        return plan.interval === interval && plan.currency === currency;
      });

      return `${getPrice(
        pricePlan?.price || 0,
        pricePlan?.currency
      )}/ ${interval}`;
    };

    const nextTier = getNextTier(tierList, subscription);
    if (nextTier) {
      return {
        tierLabel: getTierLabel(nextTier.tier, subscriptionTiersDDL),
        tierPrice: getPriceLabel(nextTier, defaultCurrency || 'USD'),
        tierStorage: `${bytesToGigabytes(nextTier.limitBytes || 0)} GB`,
      };
    }
    return undefined;
  }, [defaultCurrency, subscription, tierList, subscriptionTiersDDL]);

  const onManageSubscriptionClick = async () => {
    dispatch(subscriptionManageButtonAction());
    const { response } = await dispatch(
      manageSubscriptionEffect({ link: manageSubscriptionLink })
    ).unwrap();

    if (response) {
      openURL(response._links.portal.href, {
        isExternal: true,
      });
    }
  };

  const onUpgradeSubscriptionClick = () => {
    dispatch(subscriptionSubscribeButtonAction());
  };

  const onNotificationClose = (actionable?: boolean) => {
    setStorageQuotaStatus(storage?.quotaStatus);
    if (actionable) {
      if (subscription?.hasSubscription) {
        onManageSubscriptionClick();
        return;
      }
      onUpgradeSubscriptionClick();
      setOpenSubscriptionOverlay();
      dispatch(setShareModal({ isOpen: false }));
    }
    handleNotificationClose();
  };

  return (
    <>
      {nextTier && (
        <StorageNotificationDialog
          title="Wanna pitch like a pro?"
          subtitle={getTextByType(nextTier.tierLabel, messageType)}
          nextTier={nextTier}
          isOpen={storageOverTheLimitProps.isOpen}
          onClose={onNotificationClose}
          disableOverlay={disablePopupOverlay}
          messageType={messageType}
        />
      )}

      {nextTier && tierList && (
        <CreateSubscriptionOverlay
          tiers={tierList}
          open={openSubscriptionOverlay}
          onClose={handleSubscriptionClose}
        />
      )}
    </>
  );
};
